/*
 * CAPTURE-OWNED MODULE
 * DepartmentSelector — picker backed by iim_departments (active). Writes the
 * department key (primary_dept_key); department is optional, so "— None —"
 * yields null. Used in the Settings create + edit forms and the studio inline
 * "+ New type" (DocTypeSelector).
 *
 * Pass `departments` to drive the list from a parent (avoids redundant fetches
 * and lets the parent resolve labels); omit it to self-fetch (standalone use).
 */
import React, { useState, useEffect } from 'react';
import { captureAPI } from '../../services/api';
import './DepartmentSelector.css';

export default function DepartmentSelector({
  value,
  onChange,
  departments: deptsProp,
  disabled = false,
  label = 'Department',
  allowNone = true,
  className = '',
}) {
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(!deptsProp);

  useEffect(() => {
    if (deptsProp) return; // list supplied by parent
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await captureAPI.getDepartments();
        if (!cancelled) setFetched((res?.departments || []).filter((d) => d.is_active));
      } catch {
        if (!cancelled) setFetched([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deptsProp]);

  const departments = deptsProp || fetched;
  const hasValue = !!value && departments.some((d) => d.key === value);

  return (
    <div className={`dept-selector ${className}`}>
      {label && <label className="dept-sel-label">{label}:</label>}
      {loading ? (
        <span className="dept-sel-loading">Loading…</span>
      ) : (
        <select
          className="dept-sel-select"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        >
          {allowNone && <option value="">— None —</option>}
          {!hasValue && value && <option value={value}>{value} (inactive)</option>}
          {departments.map((d) => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
      )}
    </div>
  );
}
